import React, { useState, useCallback, useEffect } from 'react';
import { 
  Box, Projector, Eye, EyeOff, Trash2, Copy, Lock, Unlock,
  CheckSquare, Square, AlertTriangle, Ruler, Calculator,
  ChevronLeft, ChevronRight, ChevronDown, Layers, Monitor,
  Camera as CameraIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { useApp, formatWithUnit } from '../../context/AppContext';
import { toast } from 'sonner';

const SceneSidePanel = () => {
  const {
    sceneObjects, projectors, measurements,
    selectedObjectId, setSelectedObjectId,
    updateSceneObject, removeSceneObject,
    updateProjector, removeProjector,
    duplicateSelected, duplicateMultiSelected, toggleMeasurementVisibility, removeMeasurement, updateMeasurement,
    multiSelectIds, setMultiSelectIds,
    overlapAnalysis, calculateOverlaps,
    projectSettings
  } = useApp();

  const [collapsed, setCollapsed] = useState(false);
  const [lastClickedId, setLastClickedId] = useState(null); // Track last clicked for SHIFT range
  // Per-section expand/collapse — pure UI state, never persisted to scene data.
  // Default: every section is expanded.
  const [collapsedSections, setCollapsedSections] = useState({});

  // Get all selectable items in order (for SHIFT range selection)
  const getAllSelectableItems = useCallback(() => {
    const items = [];
    // Add projectors first
    [...projectors].sort((a, b) => a.name.localeCompare(b.name)).forEach(p => items.push(p.id));
    // Add scene objects
    sceneObjects.forEach(o => items.push(o.id));
    return items;
  }, [projectors, sceneObjects]);

  const toggleMultiSelect = useCallback((id) => {
    setMultiSelectIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    setLastClickedId(id);
  }, [setMultiSelectIds]);

  // Handle SHIFT+click range selection
  const handleShiftSelect = useCallback((clickedId) => {
    const allItems = getAllSelectableItems();
    const clickedIndex = allItems.indexOf(clickedId);
    
    if (clickedIndex === -1) return;
    
    // If no previous selection, just select the clicked item
    if (!lastClickedId || !allItems.includes(lastClickedId)) {
      setMultiSelectIds([clickedId]);
      setLastClickedId(clickedId);
      return;
    }
    
    const lastIndex = allItems.indexOf(lastClickedId);
    const startIndex = Math.min(lastIndex, clickedIndex);
    const endIndex = Math.max(lastIndex, clickedIndex);
    
    // Select all items in range
    const rangeItems = allItems.slice(startIndex, endIndex + 1);
    setMultiSelectIds(rangeItems);
  }, [getAllSelectableItems, lastClickedId, setMultiSelectIds]);

  const isMultiSelected = useCallback((id) => multiSelectIds.includes(id), [multiSelectIds]);

  // ── Projector POV (floating preview windows) ────────────────────────────
  // Track which projectors currently have a POV window open so we can show a
  // cyan highlight on the row.
  const [openPovIds, setOpenPovIds] = useState([]);
  useEffect(() => {
    const refresh = () => {
      const list = window.listProjectorPOVWindows?.() || [];
      setOpenPovIds(list.map(w => w.id));
    };
    window.addEventListener('projector-pov-windows-changed', refresh);
    refresh();
    return () => window.removeEventListener('projector-pov-windows-changed', refresh);
  }, []);
  const handleEnterPOV = useCallback((projectorId, projectorName) => {
    window.dispatchEvent(new CustomEvent('projector-pov-open', { detail: { id: projectorId, name: projectorName } }));
  }, []);
  const isPovOpen = useCallback((id) => openPovIds.includes(id), [openPovIds]);

  const selectAllObjects = useCallback(() => {
    setMultiSelectIds([...sceneObjects.map(o => o.id), ...projectors.map(p => p.id)]);
  }, [sceneObjects, projectors, setMultiSelectIds]);

  const clearMultiSelect = useCallback(() => setMultiSelectIds([]), [setMultiSelectIds]);

  const handleMultiDelete = useCallback(() => {
    if (multiSelectIds.length === 0) return;
    multiSelectIds.forEach(id => {
      if (projectors.find(p => p.id === id)) removeProjector(id);
      else removeSceneObject(id);
    });
    setMultiSelectIds([]);
    toast.success(`Deleted ${multiSelectIds.length} items`);
  }, [multiSelectIds, projectors, removeProjector, removeSceneObject, setMultiSelectIds]);

  const handleMultiDuplicate = useCallback(() => {
    if (multiSelectIds.length === 0) return;
    duplicateMultiSelected(multiSelectIds);
  }, [multiSelectIds, duplicateMultiSelected]);

  const handleShowAll = useCallback(() => {
    sceneObjects.forEach(obj => updateSceneObject(obj.id, { visible: true }));
    projectors.forEach(proj => updateProjector(proj.id, { power_on: true }));
    toast.success('All objects visible');
  }, [sceneObjects, projectors, updateSceneObject, updateProjector]);

  if (collapsed) {
    return (
      <div className="scene-side-panel-collapsed" data-testid="scene-side-panel">
        <Button variant="ghost" size="icon" className="h-10 w-6" onClick={() => setCollapsed(false)}
          data-testid="expand-scene-panel">
          <ChevronRight className="w-4 h-4" />
        </Button>
        <span className="scene-side-label"><Layers className="w-3 h-3" /></span>
      </div>
    );
  }

  const groupedObjects = {
    truss: sceneObjects.filter(o => o.type === 'truss').sort((a, b) => a.name.localeCompare(b.name)),
    dome: sceneObjects.filter(o => o.type === 'dome').sort((a, b) => a.name.localeCompare(b.name)),
    screen: sceneObjects.filter(o => o.type === 'screen' || o.type === 'scrim_screen').sort((a, b) => a.name.localeCompare(b.name)),
    model: sceneObjects.filter(o => o.type === 'imported' || o.type === 'object' || (!['truss', 'dome', 'screen', 'scrim_screen', 'pipe', 'box', 'sphere', 'cylinder', 'plane'].includes(o.type))).sort((a, b) => a.name.localeCompare(b.name)),
    primitive: sceneObjects.filter(o => ['pipe', 'box', 'sphere', 'cylinder', 'plane'].includes(o.type)).sort((a, b) => a.name.localeCompare(b.name))
  };
  const sortedProjectors = [...projectors].sort((a, b) => a.name.localeCompare(b.name));
  const sortedMeasurements = [...(measurements || [])].sort((a, b) => a.name.localeCompare(b.name));

  const renderObjectRow = (obj, isProjector = false, isMeasurement = false) => (
    <div key={obj.id}
      className={`scene-object-item ${selectedObjectId === obj.id ? 'selected' : ''} ${isMultiSelected(obj.id) ? 'ring-1 ring-fuchsia-500' : ''} ${isProjector && isPovOpen(obj.id) ? 'ring-1 ring-cyan-400 bg-cyan-500/10' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (isMeasurement) return;
        
        // Handle CTRL/CMD click - add/remove individual item to multi-select
        if (e.ctrlKey || e.metaKey) {
          toggleMultiSelect(obj.id);
          return;
        }
        
        // Handle SHIFT click - range select from last clicked to current
        if (e.shiftKey) {
          handleShiftSelect(obj.id);
          return;
        }
        
        // Normal click - show properties for this item, don't change multi-selection
        setSelectedObjectId(obj.id);
        setLastClickedId(obj.id);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (window.focusOnObject) window.focusOnObject(obj.id);
      }}
      data-testid={`scene-${isMeasurement ? 'measurement' : isProjector ? 'projector' : 'object'}-${obj.id}`}
    >
      {!isMeasurement && (
        <input type="checkbox" checked={isMultiSelected(obj.id)}
          onClick={(e) => { e.stopPropagation(); }}
          onChange={(e) => { e.stopPropagation(); toggleMultiSelect(obj.id); }}
          className="w-3 h-3 mr-1" />
      )}
      {isMeasurement ? <Ruler className="w-3 h-3 flex-shrink-0 text-green-400" /> :
       isProjector ? <Projector className={`w-4 h-4 flex-shrink-0 ${obj.power_on ? 'text-fuchsia-400' : 'text-zinc-600'}`} /> :
       obj.type === 'led_screen' ? <Monitor className="w-4 h-4 flex-shrink-0 text-cyan-400" /> :
       <Box className="w-4 h-4 flex-shrink-0 text-zinc-400" />}
      <span className="text-xs flex-1 truncate min-w-0">{obj.name}</span>
      {isMeasurement && obj.distance != null && (
        <span className="text-[9px] font-mono text-green-400/70 flex-shrink-0 max-w-[50px] truncate" title={formatWithUnit(obj.distance, projectSettings.unit)}>{formatWithUnit(obj.distance, projectSettings.unit)}</span>
      )}
      {isMeasurement && (
        <input
          type="color"
          value={obj.lineColor || '#00ff00'}
          onChange={(e) => { e.stopPropagation(); updateMeasurement(obj.id, { lineColor: e.target.value }); }}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 flex-shrink-0 cursor-pointer border-0 p-0 bg-transparent"
          title="Line color"
          data-testid={`measurement-color-${obj.id}`}
        />
      )}
      {isProjector && obj.out_of_range && <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" title="Out of range" />}
      {isProjector && (
        <Button
          variant="ghost"
          size="icon"
          className={`h-5 w-5 flex-shrink-0 ${isPovOpen(obj.id) ? 'text-cyan-400' : 'text-zinc-300 hover:text-cyan-300'}`}
          onClick={(e) => {
            e.stopPropagation();
            handleEnterPOV(obj.id, obj.name);
          }}
          title={isPovOpen(obj.id) ? 'Close POV Window' : 'Open POV Window'}
          data-testid={`scene-projector-pov-${obj.id}`}
        >
          <CameraIcon className="w-3 h-3" />
        </Button>
      )}
      {!isMeasurement && (
        <Button variant="ghost" size="icon"
          className={`h-5 w-5 flex-shrink-0 ${obj.locked ? 'text-amber-400' : 'text-zinc-300 hover:text-white'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isProjector) updateProjector(obj.id, { locked: !obj.locked });
            else updateSceneObject(obj.id, { locked: !obj.locked });
          }} title={obj.locked ? "Unlock" : "Lock"}>
          {obj.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
        </Button>
      )}
      <Button variant="ghost" size="icon" className="h-5 w-5 flex-shrink-0 text-zinc-300 hover:text-white"
        onClick={(e) => {
          e.stopPropagation();
          if (isMeasurement) toggleMeasurementVisibility(obj.id);
          else if (isProjector) updateProjector(obj.id, { visible: obj.visible === false });
          else updateSceneObject(obj.id, { visible: !obj.visible });
        }} title={obj.visible !== false ? "Hide" : "Show"}>
        {obj.visible !== false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3 text-zinc-500" />}
      </Button>
      <Button variant="ghost" size="icon" className="h-5 w-5 flex-shrink-0 text-zinc-300 hover:text-red-400"
        onClick={(e) => {
          e.stopPropagation();
          if (isMeasurement) removeMeasurement(obj.id);
          else if (isProjector) removeProjector(obj.id);
          else removeSceneObject(obj.id);
        }} title="Delete">
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  );

  const renderSection = (title, icon, items, isProjector = false, isMeasurement = false) => {
    if (items.length === 0) return null;
    const isCollapsed = !!collapsedSections[title];
    const toggle = () => setCollapsedSections(prev => ({ ...prev, [title]: !prev[title] }));
    return (
      <div data-testid={`scene-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <button
          type="button"
          onClick={toggle}
          className="properties-section-title mb-2 w-full flex items-center gap-2 text-left hover:text-white transition-colors"
          data-testid={`scene-section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
          title={isCollapsed ? 'Expand section' : 'Collapse section'}
        >
          {isCollapsed
            ? <ChevronRight className="w-3 h-3 text-zinc-500 flex-shrink-0" />
            : <ChevronDown className="w-3 h-3 text-zinc-500 flex-shrink-0" />}
          {icon} <span>{title} ({items.length})</span>
        </button>
        {!isCollapsed && (
          <div className="space-y-1">
            {items.map(obj => renderObjectRow(obj, isProjector, isMeasurement))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="scene-side-panel" data-testid="scene-side-panel">
      <div className="panel-header flex-shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-fuchsia-400" />
          <h3>Scene</h3>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setCollapsed(true)}
          data-testid="collapse-scene-panel">
          <ChevronLeft className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex items-center gap-1 px-2 py-1 flex-wrap border-b border-zinc-700/50">
        <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" onClick={selectAllObjects}>
          <CheckSquare className="w-3 h-3 mr-0.5" /> All
        </Button>
        <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" onClick={clearMultiSelect}
          disabled={multiSelectIds.length === 0}>
          <Square className="w-3 h-3 mr-0.5" /> Clear
        </Button>
        <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" onClick={handleShowAll}>
          <Eye className="w-3 h-3 mr-0.5" /> Show
        </Button>
        {multiSelectIds.length > 0 && (
          <>
            <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" onClick={handleMultiDuplicate}>
              <Copy className="w-3 h-3 mr-0.5" /> ({multiSelectIds.length})
            </Button>
            <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5 hover:text-red-400" onClick={handleMultiDelete}>
              <Trash2 className="w-3 h-3 mr-0.5" /> ({multiSelectIds.length})
            </Button>
          </>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-3">
          {renderSection('Projectors', <Projector className="w-4 h-4 text-fuchsia-400" />, sortedProjectors, true)}
          {renderSection('3D Models', <Box className="w-4 h-4 text-blue-400" />, groupedObjects.model)}
          {renderSection('Truss', <Box className="w-4 h-4 text-gray-400" />, groupedObjects.truss)}
          {renderSection('Screens', <Box className="w-4 h-4 text-cyan-400" />, groupedObjects.screen)}
          {renderSection('Domes', <Box className="w-4 h-4 text-purple-400" />, groupedObjects.dome)}
          {renderSection('Primitives', <Box className="w-4 h-4 text-orange-400" />, groupedObjects.primitive)}
          {renderSection('Measurements', <Ruler className="w-4 h-4 text-green-400" />, sortedMeasurements, false, true)}

          {sceneObjects.length === 0 && projectors.length === 0 && (!measurements || measurements.length === 0) && (
            <div className="text-xs text-zinc-500 text-center py-4">
              No objects in scene. Add projectors or primitives to get started.
            </div>
          )}

          {projectors.length >= 2 && (
            <div>
              <div className="properties-section-title mb-2 flex items-center justify-between">
                <span>Overlap Analysis</span>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={calculateOverlaps}
                  data-testid="calculate-overlaps-btn">
                  <Calculator className="w-3 h-3 mr-1" /> Calculate
                </Button>
              </div>
              {overlapAnalysis && overlapAnalysis.length > 0 ? (
                <div className="space-y-2">
                  {overlapAnalysis.map((overlap, idx) => (
                    <div key={`${overlap.projector1}-${overlap.projector2}`} className="p-2 bg-zinc-800 rounded text-xs">
                      <div className="font-medium text-fuchsia-400">
                        {overlap.projector1} &harr; {overlap.projector2}
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-2 text-zinc-400">
                        <div>Overlap: <span className="text-white font-mono">{overlap.overlapPercent}%</span></div>
                        <div>Pixels: <span className="text-white font-mono">{overlap.overlapPixels.x}x{overlap.overlapPixels.y}</span></div>
                        <div className="col-span-2">
                          Size: <span className="text-white font-mono">{formatWithUnit(overlap.overlapSize.width, projectSettings.unit)} x {formatWithUnit(overlap.overlapSize.height, projectSettings.unit)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : overlapAnalysis ? (
                <div className="text-xs text-zinc-500 text-center py-2">No overlaps detected</div>
              ) : null}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default SceneSidePanel;
